if (!customElements.get('currency-localization-modal')) {
  customElements.define(
    'currency-localization-modal',
    class CurrencyLocalizationModal extends HTMLElement {
      constructor() {
        super();
        this.dialog = this.querySelector('[role="dialog"]');
        this.form = this.querySelector('form');
        this.search = this.querySelector('[data-currency-localization-search]');
        this.countries = Array.from(this.querySelectorAll('[data-country-search]'));
        this.scrollContainer = this.querySelector('.currency-localization-modal__scroll');
        this.emptyState = this.querySelector('[data-currency-localization-empty]');
        this.status = this.querySelector('[data-currency-localization-status]');
        this.openedBy = null;
        this.bodyWasLocked = false;
        this.isSubmitting = false;

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeyup = this.onDocumentKeyup.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this.onSearchKeydown = this.onSearchKeydown.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
      }

      connectedCallback() {
        document.addEventListener('click', this.onDocumentClick);
        this.search?.addEventListener('input', this.onSearch);
        this.search?.addEventListener('keydown', this.onSearchKeydown);
        this.form?.addEventListener('submit', this.onSubmit);

        if (window.location.hash === '#country-selector') {
          window.requestAnimationFrame(() => this.open());
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keyup', this.onDocumentKeyup);
        this.search?.removeEventListener('input', this.onSearch);
        this.search?.removeEventListener('keydown', this.onSearchKeydown);
        this.form?.removeEventListener('submit', this.onSubmit);
      }

      onDocumentClick(event) {
        const trigger = event.target.closest('[data-currency-localization-trigger], a[href*="#country-selector"]');
        if (trigger) {
          event.preventDefault();
          let returnFocusTarget = trigger;
          const mobileHeader = trigger.closest('head-room.header-menu-open');

          if (
            trigger.classList.contains('currency-localization-trigger--mobile-menu') &&
            mobileHeader &&
            typeof mobileHeader.closeMenu === 'function'
          ) {
            returnFocusTarget = mobileHeader.querySelector('[data-burger]') || trigger;
            mobileHeader.closeMenu({ immediate: true });
          }

          this.open(returnFocusTarget);
          return;
        }

        if (!this.hidden && event.target.closest('[data-currency-localization-close]')) {
          event.preventDefault();
          this.close();
        }
      }

      onDocumentKeyup(event) {
        if (event.code.toUpperCase() === 'ESCAPE') this.close();
      }

      open(trigger = null) {
        if (!this.hidden) return;

        this.openedBy = trigger || document.activeElement;
        this.bodyWasLocked = document.body.classList.contains('overflow-hidden');
        this.hidden = false;
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keyup', this.onDocumentKeyup);
        this.scrollSelectedIntoView();

        if (typeof trapFocus === 'function') {
          trapFocus(this.dialog, this.search || this.dialog);
        } else {
          (this.search || this.dialog).focus();
        }
      }

      close() {
        if (this.hidden) return;

        this.hidden = true;
        this.setAttribute('aria-hidden', 'true');
        document.removeEventListener('keyup', this.onDocumentKeyup);
        if (!this.bodyWasLocked) document.body.classList.remove('overflow-hidden');

        if (this.search) {
          this.search.value = '';
          this.filterCountries('');
        }

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.openedBy);
        } else {
          this.openedBy?.focus();
        }
      }

      onSearch(event) {
        this.filterCountries(event.currentTarget.value);
      }

      onSearchKeydown(event) {
        if (event.key === 'Enter') event.preventDefault();
      }

      filterCountries(value) {
        const query = this.normalize(value);
        let visibleCount = 0;

        this.countries.forEach((country) => {
          const isVisible = this.normalize(country.dataset.countrySearch).includes(query);
          country.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });

        if (this.emptyState) this.emptyState.hidden = visibleCount !== 0;
        if (this.status) {
          this.status.textContent =
            visibleCount === 1
              ? this.dataset.resultsOne
              : this.dataset.resultsOther.replace('[count]', visibleCount);
        }
        if (this.scrollContainer) this.scrollContainer.scrollTop = 0;
      }

      normalize(value) {
        return value
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase()
          .trim();
      }

      scrollSelectedIntoView() {
        const selectedCountry = this.querySelector('[data-current-country-option]');
        const selectedRow = selectedCountry?.closest('[data-country-search]');
        if (!selectedRow || !this.scrollContainer) return;

        const containerRect = this.scrollContainer.getBoundingClientRect();
        const selectedRect = selectedRow.getBoundingClientRect();
        const selectedOffset =
          selectedRect.top -
          containerRect.top -
          (containerRect.height - selectedRect.height) / 2;

        this.scrollContainer.scrollTop = Math.max(
          0,
          this.scrollContainer.scrollTop + selectedOffset
        );
      }

      onSubmit(event) {
        const selectedCountry = event.submitter?.matches('[data-currency-localization-country]')
          ? event.submitter
          : null;

        if (!selectedCountry) {
          event.preventDefault();
          return;
        }

        if (this.isSubmitting) {
          event.preventDefault();
          return;
        }

        if (selectedCountry.value === this.dataset.currentCountry) {
          event.preventDefault();
          this.close();
          return;
        }

        this.isSubmitting = true;
        this.classList.add('is-submitting');
        this.querySelectorAll('[data-currency-localization-country]').forEach((country) => {
          country.setAttribute('aria-disabled', 'true');
        });
        if (this.search) this.search.disabled = true;
      }
    }
  );
}
